/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Pagination, PaginationItem, PaginationLink, Form, FormGroup, Col, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Toggle from 'react-toggle';
import Alert from 'react-s-alert';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';

// import services
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';

const Forms = props => {

    // variables
    let timeout = 0;

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [isLoading3, setIsLoading3] = useState(false);
    const [isLoading4, setIsLoading4] = useState(false);
    const [forms, setForms] = useState([]);
    const [formTypes, setFormTypes] = useState({ clientForm: "", teenForm: "", adultForm: "", coupleForm: "" });
    const [modal, setModal] = useState({ isVisible: false, isUpdate: false, selectedIndex: -1, name: "" });
    const [confirmationModal, setConfirmationModal] = useState({ isVisible: false, isDelete: false, selectedIndex: -1 });
    const [settingsModal, setSettingsModal] = useState({ isVisible: false, clientForm: "", teenForm: "", adultForm: "", coupleForm: "" });
    const [pagination, setPagination] = useState({ limit: 10, page: 1 });

    // contexts
    const { theme } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        getForms();
        getFormType();
    }, [pagination.page, pagination.limit, pagination.searchVal]);

    const getForms = () => {
        if (!isLoading) {
            setIsLoading(true);
            AdminService.getForms(pagination.limit, pagination.page, pagination.searchVal).then(data => {
                try {
                    setForms(data.forms);
                    if (data.pagination.totalPages <= data.pagination.page) {
                        data.pagination.page = data.pagination.totalPages;
                    }
                    setPagination(data.pagination);
                    setIsLoading(false);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Admin Forms useEffect', 'Error in try catch block', err.stack);
                    setIsLoading(false);
                }
            });
        }
    }

    const getFormType = () => {
        try {
            AdminService.getFormType().then(data => {
                setFormTypes({ clientForm: data.forms.clientForm, teenForm: data.forms.teenForm, adultForm: data.forms.adultForm, coupleForm: data.forms.coupleForm });
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms getFormType', 'Error in try catch block', err.stack);
        }
    }

    // handle functions
    const handleSearch = e => {
        try {
            let value = e.target.value;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                setPagination({ ...pagination, searchVal: value });
            }, 500);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleSearch', 'Error in try catch block', err.stack);
        }
    }

    const handlePagination = (e, index) => {
        e.preventDefault();
        try {
            setPagination({ ...pagination, page: index });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handlePagination', 'Error in try catch block', err.stack);
        }
    }

    const handleToggleChange = (index) => {
        try {
            setIsLoading3(true);
            let tempForms = [...forms];
            tempForms[index].isActive = !tempForms[index].isActive;
            setForms(tempForms);
            AdminService.setActiveStatusForm(tempForms[index]._id, tempForms[index].isActive)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setConfirmationModal({ isVisible: false, isDelete: false, selectedIndex: -1 });
                    isLoading3(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    isLoading3(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleToggleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleFormSave = () => {
        try {
            setIsLoading4(true);
            AdminService.addForm({ name: modal.name }).then(data => {
                getForms();
                setModal({ isVisible: false, isUpdate: false, selectedIndex: -1, name: "" });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading4(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading4(false)
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormSave', 'Error in try catch block', err.stack);
        }
    }

    const handleFormUpdate = () => {
        try {
            setIsLoading4(true);
            AdminService.updateForm({ id: forms[modal.selectedIndex]._id, name: modal.name }).then(data => {
                getForms();
                setModal({ isVisible: false, isUpdate: false, selectedIndex: -1, name: "" });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading4(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading4(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormUpdate', 'Error in try catch block', err.stack);
        }
    }

    const handleFormTypeUpdate = () => {
        try {
            setIsLoading2(true);
            AdminService.updateFormType(settingsModal).then(data => {
                getFormType();
                setSettingsModal({ isVisible: false, clientForm: "", teenForm: "", adultForm: "", coupleForm: "" });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormTypeUpdate', 'Error in try catch block', err.stack);
        }
    }

    const handleFormDelete = (id) => {
        try {
            setIsLoading3(true);
            AdminService.deleteForm(id).then(data => {
                getForms();
                setConfirmationModal({ isVisible: false, isDelete: false, id: "" });
                setModal({ isVisible: false, isUpdate: false, data: { ...modal.data, question: "", answers: [], orderNo: "" } });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading3(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading3(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormDelete', 'Error in try catch block', err.stack);
        }
    }

    //utility functions
    const showQuestions = (index) => {
        try {
            props.setSelectedForm({ id: forms[index]._id, name: forms[index].name });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms showQuestions', 'Error in try catch block', err.stack);
        }
    }

    return (
        <main>
            <div className="container-fluid">
                <h3 className="my-4">Forms</h3>
                <div className="card mb-4">
                    <div className="card-header d-flex">
                        <label className="my-auto">Form List</label>
                        <Button className="ml-auto my-auto px-3" type="button" style={Object.assign({ height: 'fit-content' }, theme.formButton)} onClick={() => setModal({ isVisible: true, isUpdate: false, selectedIndex: -1, name: "" })}>Add</Button>
                        <Button className="ml-2 my-auto px-3" type="button" style={Object.assign({ height: 'fit-content' }, theme.formButton)} onClick={() => setSettingsModal({ isVisible: true, clientForm: formTypes.clientForm, teenForm: formTypes.teenForm, adultForm: formTypes.adultForm, coupleForm: formTypes.coupleForm })}><i className="fas fa-cog"></i></Button>
                    </div>
                    <div className="card-body py-0">
                        <div className="table-responsive">
                            <table className="table table-bordered mb-0" id="dataTable" width="100%" cellSpacing="0">
                                <thead>
                                    <tr>
                                        <td colSpan="5" className="border-0 px-0">
                                            <div className="d-flex">
                                                <div className="ml-auto">
                                                    Search<input type="text" id="searchInput" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input>
                                                    <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput").value = ""; setPagination({ ...pagination, searchVal: "" }); }}><i className="fa fa-times"></i></span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Name</th>
                                        <th align="center" style={{ width: '185px', textAlign: "center" }}>Activate / Deactivate</th>
                                        <th align="center" style={{ textAlign: "center" }} colSpan="2">Actions</th>
                                        <th style={{ width: '150px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        forms.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Forms Table${index}`}>
                                                <tr key={index}>
                                                    <td>{item.name}</td>
                                                    <td align="center"><Toggle icons={false} checked={item.isActive} onChange={() => setConfirmationModal({ isVisible: true, isDelete: false, selectedIndex: index })} /></td>
                                                    <td align="center" style={{ width: "1px" }}><span style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: true, isUpdate: true, selectedIndex: index, name: item.name })}><i className="fas fa-edit"></i></span></td>
                                                    <td align="center" style={{ width: "1px" }}><span style={{ cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: true, isDelete: true, selectedIndex: index })}><i className="far fa-trash-alt"></i></span></td>
                                                    <td align="center"><a style={{ color: "#384E73" }} href="#" onClick={() => showQuestions(index)}>View questions</a></td>
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5" className="border-0 pb-0 px-0">
                                            <div className="d-flex">
                                                <ErrorBoundary errorAt="At Admin Forms Pagination">
                                                    <div className="p-2">Showing {pagination.totalResults == 0 ? 0 : (1 + (10 * (pagination.page - 1)))} to {(pagination.limit + (10 * (pagination.page - 1))) > pagination.totalResults ? pagination.totalResults : (pagination.limit + (10 * (pagination.page - 1)))} of {pagination.totalResults} entries</div>
                                                    <Pagination aria-label="Page navigation example" className="ml-auto">
                                                        <PaginationItem disabled={pagination.page == 1}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page - 1)}
                                                                previous
                                                                href="#" />
                                                        </PaginationItem>
                                                        {[...Array(pagination.totalPages)].map((page, i) =>
                                                            <PaginationItem active={i + 1 === pagination.page} key={i}>
                                                                <PaginationLink onClick={e => handlePagination(e, i + 1)} href="#">
                                                                    {i + 1}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        )}
                                                        <PaginationItem disabled={pagination.page == pagination.totalPages}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page + 1)}
                                                                next
                                                                href="#" />
                                                        </PaginationItem>
                                                    </Pagination>
                                                </ErrorBoundary>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <ErrorBoundary errorAt="At Admin Forms SettingsModal">
                <Modal size="lg" isOpen={settingsModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setSettingsModal({ isVisible: false, clientForm: "", teenForm: "", adultForm: "", coupleForm: "" })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Settings</span></ModalHeader>
                    <ModalBody>
                        <label className="mb-4">
                            The form you select will be sent to user as per respective names via email.
                        </label>
                        <Form>
                            <FormGroup row>
                                <Label sm={4} for="select">Client form</Label>
                                <Col sm={6}>
                                    <Input type="select" value={settingsModal.clientForm} onChange={(e) => setSettingsModal({ ...settingsModal, clientForm: e.target.value })}>
                                        <option value="">Select</option>
                                        {
                                            forms.map((item, index) => (
                                                <option key={index} value={item._id}>{item.name}</option>
                                            ))
                                        }
                                    </Input>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={4} for="select">Teen checklist</Label>
                                <Col sm={6}>
                                    <Input type="select" value={settingsModal.teenForm} onChange={(e) => setSettingsModal({ ...settingsModal, teenForm: e.target.value })}>
                                        <option value="">Select</option>
                                        {
                                            forms.map((item, index) => (
                                                <option key={index} value={item._id}>{item.name}</option>
                                            ))
                                        }
                                    </Input>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={4} for="select">Adult checklist</Label>
                                <Col sm={6}>
                                    <Input type="select" value={settingsModal.adultForm} onChange={(e) => setSettingsModal({ ...settingsModal, adultForm: e.target.value })}>
                                        <option value="">Select</option>
                                        {
                                            forms.map((item, index) => (
                                                <option key={index} value={item._id}>{item.name}</option>
                                            ))
                                        }
                                    </Input>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={4} for="select">Couple checklist</Label>
                                <Col sm={6}>
                                    <Input type="select" value={settingsModal.coupleForm} onChange={(e) => setSettingsModal({ ...settingsModal, coupleForm: e.target.value })}>
                                        <option value="">Select</option>
                                        {
                                            forms.map((item, index) => (
                                                <option key={index} value={item._id}>{item.name}</option>
                                            ))
                                        }
                                    </Input>
                                </Col>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleFormTypeUpdate()} disabled={isLoading2}>
                            {isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Admin Forms ConfirmationModal">
                <Modal size="sm" isOpen={confirmationModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: false, isDelete: false, selectedIndex: -1 })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Are you sure?</span></ModalHeader>
                    <ModalBody>
                        {
                            confirmationModal.selectedIndex >= 0 ?
                                confirmationModal.isDelete ?
                                    <Label>By clicking on Save button <b>{forms[confirmationModal.selectedIndex].name}</b> will be <b>Deleted</b>.</Label>
                                    :
                                    <Label>By clicking on Save button <b>{forms[confirmationModal.selectedIndex].name}</b> will be <b>{forms[confirmationModal.selectedIndex].isActive ? "Deactivated" : "Activated"}</b>.</Label>
                                : null
                        }
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => confirmationModal.isDelete ? handleFormDelete(forms[confirmationModal.selectedIndex]._id) : handleToggleChange(confirmationModal.selectedIndex)} disabled={isLoading3}>
                            {isLoading3 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Admin Forms Modal">
                <Modal size="sm" isOpen={modal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: false, isUpdate: false, selectedIndex: -1, formName: "" })}><i className="fas fa-times"></i></span>}>
                        <span style={theme.headingBold}>{modal.isUpdate ? "Update form" : "Add form"}</span>
                    </ModalHeader>
                    <ModalBody>
                        <Label>Enter form name</Label>
                        <Input type="text" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} />
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => modal.isUpdate ? handleFormUpdate() : handleFormSave()} disabled={isLoading4}>
                            {isLoading4 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}{modal.isUpdate ? "Update" : "Save"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
        </main>
    )

}

export default Forms;